import React, { useEffect, useState } from 'react';
import useIntl from '@afosto/utils-react/intl/useIntl';
import usePrivate from '@afosto/auth-react/usePrivate';
import usePermissions from '@afosto/utils-react/permissions/usePermissions';
import { useConfirm } from '@afosto/app-components-react/ConfirmDialog';
import { Link, Route, Switch, useHistory, useLocation, useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { FormattedMessage } from 'react-intl';
import Alert from '@material-ui/lab/Alert';
import EditIntegrationDrawer from '../../components/EditIntegrationDrawer';
import IntegrationDetails from './IntegrationDetails';
import IntegrationLogs from './IntegrationLogs';
import IntegrationProvider from './IntegrationProvider';
import Page from '@afosto/app-components-react/Page';
import { apps as appsApi } from '@afosto/apps/api/Apps';
import { integrations as integrationsApi } from '@afosto/apps/api/Integrations';
import translations from './translations';

const ViewIntegration = () => {
  usePrivate();

  const confirm = useConfirm();
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();
  const { id } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const [hasPermissions, UnauthorizedComponent] = usePermissions(['app:integrations:read']);
  const [app, setApp] = useState(null);
  const [integration, setIntegration] = useState(null);
  const [isFetchingIntegration, setIsFetchingIntegration] = useState(true);
  const [showEditIntegration, setShowEditIntegration] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const fetchIntegration = async () => {
      setIsFetchingIntegration(true);

      try {
        const response = await integrationsApi.getIntegration(id);
        const { data } = response ?? {};

        const appResponse = await appsApi.getApp(data?.app_code).catch(() => {});
        const { data: appData } = appResponse ?? {};

        if (isMounted) {
          setApp(appData);
          setIntegration(data);
        }
      } catch (err) {
        if (isMounted) {
          setApp(null);
          setIntegration(null);
        }
      } finally {
        if (isMounted) {
          setIsFetchingIntegration(false);
        }
      }
    };

    if (id) {
      fetchIntegration();
    }

    return () => {
      isMounted = false;
    };
  }, [id]);

  const handleSaveIntegration = updatedIntegration => {
    setIntegration(updatedIntegration);
    setShowEditIntegration(false);
  };

  const handleDeleteIntegration = async () => {
    try {
      const confirmResponse = await confirm({
        title: intl.formatMessage(translations.confirmDeleteIntegration.title),
        description: intl.formatMessage(translations.confirmDeleteIntegration.description),
        catchOnCancel: false,
        buttons: [
          {
            label: intl.formatMessage(translations.confirmDeleteIntegration.buttons.cancel),
            type: 'cancel',
            variant: 'outlined',
            style: {
              marginRight: 'auto',
            },
          },
          {
            label: intl.formatMessage(translations.confirmDeleteIntegration.buttons.delete),
            response: 'deleteIntegration',
            type: 'submit',
            variant: 'contained',
            color: 'primary',
          },
        ],
      });

      if (confirmResponse !== 'deleteIntegration') {
        return;
      }

      await integrationsApi.disable(integration.id);

      history.push('/integrations');

      enqueueSnackbar(intl.formatMessage(translations.feedback.integrationDeleted), {
        variant: 'success',
      });
    } catch (err) {
      enqueueSnackbar(intl.formatMessage(translations.feedback.integrationNotDeleted), {
        variant: 'error',
      });
    }
  };

  if (!hasPermissions()) {
    return (
      <Page title={intl.formatMessage(translations.unknownIntegration.title)} fullWidth>
        <UnauthorizedComponent />
      </Page>
    );
  }

  const secondaryActions = [
    ...(integration && hasPermissions(['app:integrations:write'])
      ? [
          {
            text: intl.formatMessage(translations.secondaryActions.deleteIntegration),
            action: handleDeleteIntegration,
            color: 'secondary',
          },
        ]
      : []),
  ];

  return (
    <IntegrationProvider value={{ app, integration }}>
      <Page
        title={
          !isFetchingIntegration && !integration?.id
            ? intl.formatMessage(translations.unknownIntegration.title)
            : integration?.name
        }
        isLoading={isFetchingIntegration}
        subNavigation={{
          current: location.pathname,
          tabs: [
            {
              label: intl.formatMessage(translations.subNavigation.details),
              path: `/integrations/${id}`,
            },
            {
              label: intl.formatMessage(translations.subNavigation.logs),
              path: `/integrations/${id}/logs`,
            },
          ],
          onChange: (e, value) => history.replace(value),
        }}
        breadcrumbs={[
          {
            label: intl.formatMessage(translations.breadcrumbs.integrations),
            path: '/integrations',
          },
          {
            label: integration?.name,
            path: `/integrations/${integration?.id}`,
            active: true,
          },
        ]}
        primaryAction={
          integration && app && hasPermissions(['app:integrations:write'])
            ? {
                text: intl.formatMessage(translations.primaryAction.label),
                action: () => setShowEditIntegration(true),
                icon: ['fal', 'pencil'],
              }
            : null
        }
        secondaryActions={secondaryActions}
        linkComponent={Link}
        fullWidth
      >
        {!isFetchingIntegration && !integration?.id && (
          <Alert severity="error">
            <FormattedMessage {...translations.unknownIntegration.message} />
          </Alert>
        )}

        {!isFetchingIntegration && !!integration && (
          <Switch>
            <Route component={IntegrationLogs} path="/integrations/:id/logs" />
            <Route component={IntegrationDetails} path="/integrations/:id" />
          </Switch>
        )}
      </Page>
      <EditIntegrationDrawer
        app={app}
        integration={integration}
        isOpen={showEditIntegration}
        onClose={() => setShowEditIntegration(false)}
        onSave={handleSaveIntegration}
      />
    </IntegrationProvider>
  );
};

export default ViewIntegration;
